import { type ReactNode } from 'react';

interface MaintenanceCardProps {
  title: string;
  /** Full sentence on desktop; the artboard trims it on mobile. */
  body: string;
  bodyShort: string;
  /** The measured line between copy and action — «Останній запуск», «Під видалення зараз». */
  meterLabel: string;
  meterLabelShort: string;
  meterValue: string;
  /** Destructive cards carry the artboard's 3px danger rule along their top edge. */
  destructive?: boolean;
  children: ReactNode;
}

/**
 * One maintenance card on the `Redesign-D-Admin` settings artboard: heading,
 * one paragraph of what it does, a hairline-bounded fact row, then the action.
 *
 * The artboard sets an illustration to the right of the heading
 * (`assets/cf/illu/clock.png`, `truck_empty.png`). Those files are not in
 * `apps/admin/public/`, and that directory is outside this run's approved file
 * list — the slot is left out rather than filled with a stand-in.
 */
export function MaintenanceCard({
  title,
  body,
  bodyShort,
  meterLabel,
  meterLabelShort,
  meterValue,
  destructive = false,
  children,
}: MaintenanceCardProps) {
  return (
    <section
      className={`flex flex-col gap-2.5 rounded-cf-lg border border-cf-line bg-cf-surface p-4 lg:gap-3 lg:px-6 lg:py-[22px] ${
        destructive ? 'shadow-[inset_0_3px_0_var(--color-cf-danger)]' : ''
      }`}
    >
      <div>
        <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[18px]">{title}</h2>
        <p className="mt-1.5 text-[13px] leading-[1.5] text-cf-ink-muted">
          <span className="lg:hidden">{bodyShort}</span>
          <span className="hidden lg:inline">{body}</span>
        </p>
      </div>

      <div className="flex justify-between border-t border-cf-line-soft pt-2 text-xs lg:border-b lg:border-cf-line-soft lg:py-2.5 lg:text-[13px]">
        <span className="text-cf-ink-subtle">
          <span className="lg:hidden">{meterLabelShort}</span>
          <span className="hidden lg:inline">{meterLabel}</span>
        </span>
        <span className="font-mono">{meterValue}</span>
      </div>

      {children}
    </section>
  );
}
